import { Fragment, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useStore } from '../store';
import { useActions } from './Workspace';
import { IconArrowDown, IconArrowUp, IconArrowUpRight, IconCornerDownLeft, IconDeviceDesktop, IconFolder, IconHistory, IconLayoutColumns, IconLayoutRows, IconLayoutSidebar, IconAlignJustified, IconPlus, IconSearch, IconWorld } from '@tabler/icons-react';
import { Empty, Hint, Ic, Mark, Mono, presence, usePresence, type TablerIcon } from './ds';
import { statusMark } from './PaneBar';
import type { Session } from '../net/protocol';
import { closedAgo, sessionPlace } from './sessionMeta';
import { useNow } from './time';
import { numberedSessions } from './organize';
import { isMac, numModLabel } from './keys';
import { REDUCED_MS, reducedMotion } from './motion';

interface Item {
  id: string;
  label: string;
  group: Group;
  hint?: string;
  status?: Session['status'];
  /** A command's Tabler icon; sessions carry their status mark instead. */
  icon?: TablerIcon;
  /** ⌘1..9 for the first nine sessions, shown on the right (and matched by the search). */
  num?: number;
  run(): void;
}

/** A key cap at the palette's own size (18px, 10px type): the Esc on the input row and the footer's keys. */
const Cap = ({ children }: { children: ReactNode }) => <kbd className="ws-pal-cap">{children}</kbd>;
const CapIc = ({ icon }: { icon: TablerIcon }) => <Ic icon={icon} size={10} stroke={2} />;

/** How long the palette takes to leave, in ms (--dur-exit-lg). */
// Sections, in the order they list. The 4b canvas lists one flat run; the labels are the user's.
const GROUPS = ['Sessions', 'Terminal', 'Desktop', 'Files', 'Web', 'Workspace'] as const;
type Group = (typeof GROUPS)[number];
const EXIT_MS = 180;

/** Subsequence match score (lower is better); -1 if no match. */
function score(q: string, text: string): number {
  if (!q) return 0;
  const t = text.toLowerCase();
  const direct = t.indexOf(q);
  if (direct >= 0) return direct;
  let pos = -1;
  let gaps = 0;
  for (const ch of q) {
    const i = t.indexOf(ch, pos + 1);
    if (i < 0) return -1;
    gaps += i - pos - 1;
    pos = i;
  }
  return 100 + gaps;
}

export function Palette({ onClose }: { onClose: () => void }) {
  const a = useActions();
  const all = useStore((s) => s.sessions);
  // In ⌘1–9's order, as the sidebar lists and numbers them.
  // then the sessions other rows hold (a tab or a pane there), unnumbered: picked, they show where they are
  const sessions = useMemo(() => {
    const rows = numberedSessions(all, a.tabs);
    return [...rows, ...all.filter((s) => !rows.includes(s))];
  }, [all, a.tabs]);
  const rowCount = useMemo(() => numberedSessions(all, a.tabs).length, [all, a.tabs]);
  const closed = useStore((s) => s.closed);
  const ports = useStore((s) => s.ports);
  const desktopRunning = useStore((s) => s.desktopRunning);
  const [q, setQ] = useState('');
  // The highlighted item, by id: sessions re-ranking while the palette is open don't move it.
  const [selId, setSelId] = useState<string | null>(null);
  const now = useNow();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const items = useMemo<Item[]>(() => {
    const out: Item[] = sessions.map((s, i) => ({
      id: `s${s.id}`,
      group: 'Sessions',
      label: s.name,
      // What runs where, so "codex", a folder or a branch finds the session too.
      hint: sessionPlace(s) || 'session',
      num: i < 9 && i < rowCount ? i + 1 : undefined,
      status: s.status,
      run: () => a.selectSession(s.id),
    }));
    out.push({ id: 'new', group: 'Terminal', label: 'New session', icon: IconPlus, hint: 'create', run: () => a.startNew() });
    for (const c of closed) {
      out.push({ id: `c${c.id}`, group: 'Terminal', label: `Reopen ${c.name}`, icon: IconHistory, hint: [closedAgo(c.closedAt, now), c.row?.place].filter(Boolean).join(' · '), run: () => void a.reopen(c.id) });
    }
    out.push({ id: 'desktop', group: 'Desktop', label: 'Open desktop', icon: IconDeviceDesktop, hint: desktopRunning ? 'running' : 'stopped', run: a.openDesktop });
    if (desktopRunning) out.push({ id: 'desktop-stop', group: 'Desktop', label: 'Stop desktop stream', icon: IconDeviceDesktop, run: () => void a.stopDesktop() });
    out.push({ id: 'files', group: 'Files', label: 'Open files', icon: IconFolder, hint: 'the cloud machine’s home', run: a.openFiles });
    for (const p of ports) {
      out.push({ id: `p${p.port}`, group: 'Web', label: `Open localhost:${p.port}`, icon: IconWorld, hint: p.process, run: () => a.openExternal(p.port) });
    }
    if (a.compact) {
      // Phones: one pane, the sessions live on Home.
      out.push({ id: 'menu', group: 'Workspace', label: 'Home', icon: IconAlignJustified, hint: 'sessions', run: a.goHome });
      return out;
    }
    if (a.paneCount > 0 && a.paneCount < 4 && !a.tabs.page) {
      out.push({ id: 'split', group: 'Terminal', label: 'Split right', icon: IconLayoutColumns, hint: 'up to 4 panes', run: () => a.split('row') });
      out.push({ id: 'split-down', group: 'Terminal', label: 'Split down', icon: IconLayoutRows, hint: 'up to 4 panes', run: () => a.split('col') });
    }
    out.push({ id: 'sidebar', group: 'Workspace', label: 'Toggle sidebar', icon: IconLayoutSidebar, run: a.toggleSidebar });
    out.push({ id: 'guide', group: 'Workspace', label: 'Guide', icon: IconArrowUpRight, hint: 'how to use cmx', run: () => window.open('/guide', '_blank', 'noopener') });
    return out;
  }, [sessions, rowCount, closed, ports, desktopRunning, a, now]);

  // The matches in sections, the section with the best match first: "open d" puts Open desktop
  // before a run of fuzzy "Reopen …" rows. With no query, the sections' own order.
  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    const matches = items
      .map((it, i) => ({ it, s: score(qq, `${it.label} ${it.hint ?? ''} ${it.num ?? ''}`), i }))
      .filter((x) => x.s >= 0);
    const best = new Map<Group, number>();
    for (const x of matches) best.set(x.it.group, Math.min(best.get(x.it.group) ?? x.s, x.s));
    const rank = (g: Group) => best.get(g) ?? 0;
    return matches
      .sort((x, y) => rank(x.it.group) - rank(y.it.group) || GROUPS.indexOf(x.it.group) - GROUPS.indexOf(y.it.group) || x.s - y.s || x.i - y.i)
      .map((x) => x.it);
  }, [items, q]);

  const sel = Math.max(0, filtered.findIndex((it) => it.id === selId));
  useEffect(() => setSelId(null), [q]);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  useEffect(() => {
    if (sel === 0) listRef.current?.scrollTo({ top: 0 });
    else listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [sel]);

  // Enters from a few px above, leaves quicker; the parent unmounts us once we're gone.
  const [open, setOpen] = useState(true);
  const pres = usePresence(open, EXIT_MS);
  const { shown } = pres;
  const closing = useRef(false);
  const close = () => {
    if (closing.current) return;
    closing.current = true;
    setOpen(false);
    setTimeout(onClose, reducedMotion() ? REDUCED_MS : EXIT_MS);
  };

  const run = (it: Item | undefined) => {
    if (!it) return;
    close();
    it.run();
  };

  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown' || (e.ctrlKey && e.key === 'n')) {
      e.preventDefault();
      setSelId(filtered[Math.min(filtered.length - 1, sel + 1)]?.id ?? null);
    } else if (e.key === 'ArrowUp' || (e.ctrlKey && e.key === 'p')) {
      e.preventDefault();
      setSelId(filtered[Math.max(0, sel - 1)]?.id ?? null);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      run(filtered[sel]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  };

  const input = (
    <span className="ws-pal-in">
      <input
        ref={inputRef}
        className="ws-pal-input"
        autoCapitalize="off"
        autoCorrect="off"
        autoComplete="off"
        spellCheck={false}
        enterKeyHint="go"
        placeholder={a.compact ? 'Switch session or run…' : 'Switch session, run a command…'}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        aria-label="Command"
        aria-controls="palette-list"
        aria-activedescendant={filtered[sel] ? `pi-${filtered[sel].id}` : undefined}
        data-testid="palette-input"
      />
    </span>
  );

  return (
    <div className="ws-pal-backdrop" data-compact={a.compact ? '' : undefined} data-shown={shown ? '' : undefined} onPointerDown={close} data-testid="palette-backdrop">
      <div
        className="ws-pal ds-float"
        data-size="lg"
        {...presence(pres)}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        data-testid="palette"
        onPointerDown={(e) => e.stopPropagation()}
        onKeyDown={onKey}
      >
        {a.compact ? (
          <div className="ws-pal-top">
            <div className="ws-pal-field">
              <span className="ws-pal-search">
                <Ic icon={IconSearch} />
              </span>
              {input}
            </div>
            <button type="button" className="ws-pal-cancel" onClick={close} data-testid="palette-cancel">
              Cancel
            </button>
          </div>
        ) : (
          <div className="ws-pal-top">
            <span className="ws-pal-search">
              <Ic icon={IconSearch} size={14} />
            </span>
            {input}
            <Cap>esc</Cap>
          </div>
        )}
        <ul className="ws-pal-list" id="palette-list" role="listbox" ref={listRef}>
          {filtered.map((it, i) => (
            <Fragment key={it.id}>
              {it.group !== filtered[i - 1]?.group && (
                <li className="ws-pal-group" role="presentation">
                  <Mono className="ws-pal-group-word">{it.group}</Mono>
                </li>
              )}
            <li
              id={`pi-${it.id}`}
              role="option"
              aria-selected={i === sel}
              className="ws-pal-row"
              data-testid="palette-item"
              onPointerMove={() => setSelId(it.id)}
              onClick={() => run(it)}
            >
              <span className="ws-pal-lead">{it.status ? <Mark {...statusMark(it.status)} /> : it.icon ? <Ic icon={it.icon} /> : null}</span>
              <span className="ws-pal-text">
                <span className="ws-pal-label">{it.label}</span>
                {it.hint && <span className="ws-pal-hint">{it.hint}</span>}
              </span>
              {it.num !== undefined &&
                !a.compact &&
                (isMac() ? (
                  <Hint k={String(it.num)} mod />
                ) : (
                  <span className="ds-hint">
                    {numModLabel()}
                    {it.num}
                  </span>
                ))}
            </li>
            </Fragment>
          ))}
          {!filtered.length && (
            // nothing matches: Files' search empty (md, the magnifier over an empty sheet); a long query is cut
            <li className="ws-pal-empty" role="presentation" data-testid="palette-empty">
              <Empty art="search" size="md" title={`No matches for “${q.trim().length > 40 ? `${q.trim().slice(0, 40)}…` : q.trim()}”`} lead="Try a session’s name, or a command like New terminal." />
            </li>
          )}
        </ul>
        {!a.compact && (
          <div className="ws-pal-foot" aria-hidden="true">
            <span className="ws-pal-keys" data-pair="">
              <Cap>
                <CapIc icon={IconArrowUp} />
              </Cap>
              <Cap>
                <CapIc icon={IconArrowDown} />
              </Cap>
              <span>navigate</span>
            </span>
            <span className="ws-pal-keys">
              <Cap>
                <CapIc icon={IconCornerDownLeft} />
              </Cap>
              run
            </span>
            <span className="ws-pal-keys">
              <Cap>esc</Cap>
              close
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
